import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'

/* ---------------------------------------------------------- reading light */

export const READING_LIGHT_KEY = 'burrow-writings-reading-light'

/**
 * The reader's Reading light: off the first time, then whatever the reader
 * last chose, remembered per browser (Niki, 10/2).
 */
export function useReadingLight(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(() => {
    try {
      return window.localStorage.getItem(READING_LIGHT_KEY) === 'on'
    } catch {
      return false
    }
  })
  const set = useCallback((next: boolean) => {
    setOn(next)
    try {
      window.localStorage.setItem(READING_LIGHT_KEY, next ? 'on' : 'off')
    } catch {
      /* not remembered — fine */
    }
  }, [])
  return [on, set]
}

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

/* ----------------------------------------------------------- reading focal */

/**
 * The reading line, as a share of the viewport's height: where Follow scrolls
 * the paragraph being read to, and where the Reading light looks for it when
 * it follows the scroll. 40% down sits just above the middle, where the eye
 * rests on a page it's reading down, so a paragraph lights as it arrives
 * there and hands on as it leaves.
 */
const FOCAL_LINE = 0.4
/** How far above or below a block the pointer can be and still be on the text (the gap between paragraphs). */
const NEAR_BLOCK = 48

type Placed = { id: string; r: DOMRect; figure: boolean }

/** The `[data-focus]` blocks that have a box, and where they are. */
function placedBlocks(root: HTMLElement): Placed[] {
  const out: Placed[] = []
  root.querySelectorAll<HTMLElement>('[data-focus]').forEach((el) => {
    const r = el.getBoundingClientRect()
    if (r.height > 0 && el.dataset.focus) out.push({ id: el.dataset.focus, r, figure: el.tagName === 'FIGURE' })
  })
  return out
}

const area = (b: Placed) => b.r.width * b.r.height

/**
 * The block the reader is reading. Two guides, and the pointer wins while
 * it's on the text (Niki, 10/6):
 * - the pointer over a block lights exactly that one. Beside a floated image,
 *   the words on the left are the paragraph and the photo on the right is the
 *   image, however their boxes overlap in height. In the gap between two
 *   paragraphs the lit one stays lit until the pointer reaches the next.
 * - otherwise the light follows the scroll: the block crossing the reading
 *   line (FOCAL_LINE). That's the pointer off the text (the margins, outside
 *   the column, out of the window), no pointer at all, and touch once it scrolls.
 * Scrolling hands the light to the reading line even with the pointer resting
 * on the text: a paragraph sliding under a still cursor isn't one the reader
 * pointed at. The pointer takes it back the moment it really moves (browsers
 * can send a move for a still cursor as the page scrolls under it — one that
 * hasn't changed position is ignored).
 * Touch has no hover, so a tap chooses the block until the next scroll.
 * Nothing flickers: while the reading line crosses a gap, the lit block stays
 * lit as long as it's on screen. Off → null, and no listeners.
 */
export function useReadingFocal(rootRef: RefObject<HTMLElement | null>, enabled: boolean, contentKey: unknown): string | null {
  const [focal, setFocal] = useState<string | null>(null)
  const pointer = useRef<{ x: number; y: number } | null>(null)
  // Who leads: the pointer (it moved, or a tap) or the scroll.
  const leader = useRef<'pointer' | 'scroll'>('scroll')

  useEffect(() => {
    if (!enabled) {
      setFocal(null)
      return
    }
    let raf = 0
    let current: string | null = null
    const light = (id: string | null) => {
      current = id
      setFocal(id)
    }
    // Under the pointer: the block it's over, a paragraph gap beside the text
    // ('gap': keep what's lit), or off the text (null).
    const atPointer = (blocks: Placed[], p: { x: number; y: number }): { over: string } | 'gap' | null => {
      const hit = document.elementFromPoint?.(p.x, p.y)?.closest<HTMLElement>('[data-focus]')
      if (hit && rootRef.current?.contains(hit) && hit.dataset.focus) return { over: hit.dataset.focus }
      // No hit (no hit testing, or something drawn on top): by the boxes, the
      // smallest (the most specific) one the pointer is inside.
      let over: Placed | null = null
      let gap = false
      for (const b of blocks) {
        const inX = p.x >= b.r.left && p.x <= b.r.right
        if (inX && p.y >= b.r.top && p.y <= b.r.bottom) {
          if (!over || area(b) < area(over)) over = b
        } else if (inX && Math.min(Math.abs(p.y - b.r.top), Math.abs(p.y - b.r.bottom)) <= NEAR_BLOCK) gap = true
      }
      return over ? { over: over.id } : gap ? 'gap' : null
    }
    // On the reading line: the block crossing it (the words rather than a photo
    // floated beside them, then the smallest). With the line in a gap: what's
    // lit while it's on screen, else the block nearest the line.
    const atReadingLine = (blocks: Placed[]): string | null => {
      const vh = window.innerHeight
      const y = vh * FOCAL_LINE
      const crossing = blocks
        .filter((b) => b.r.top <= y && b.r.bottom >= y)
        .sort((a, b) => Number(a.figure) - Number(b.figure) || area(a) - area(b))
      if (crossing.length) return crossing[0].id
      const lit = blocks.find((b) => b.id === current)
      if (lit && lit.r.bottom > 0 && lit.r.top < vh) return lit.id
      let best: Placed | null = null
      let bestD = Infinity
      for (const b of blocks) {
        const d = y < b.r.top ? b.r.top - y : y - b.r.bottom
        if (d < bestD) {
          best = b
          bestD = d
        }
      }
      return best?.id ?? current
    }
    const measure = () => {
      raf = 0
      const root = rootRef.current
      if (!root) return
      const blocks = placedBlocks(root)
      const p = leader.current === 'pointer' ? pointer.current : null
      if (p) {
        const at = atPointer(blocks, p)
        if (at === 'gap' && current) return
        if (at && at !== 'gap') return light(at.over)
      }
      light(atReadingLine(blocks))
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure)
    }
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return
      const was = pointer.current
      if (was && was.x === e.clientX && was.y === e.clientY) return
      pointer.current = { x: e.clientX, y: e.clientY }
      leader.current = 'pointer'
      schedule()
    }
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return
      pointer.current = { x: e.clientX, y: e.clientY }
      leader.current = 'pointer'
      schedule()
    }
    const onScroll = () => {
      leader.current = 'scroll'
      schedule()
    }
    // The pointer left the window: it's off the text.
    const onOut = (e: MouseEvent) => {
      if (e.relatedTarget) return
      pointer.current = null
      leader.current = 'scroll'
      schedule()
    }
    // Light up where things stand: a pointer that hasn't moved since the light
    // went on is still over what it was over; without one, the reading line.
    schedule()
    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerdown', onDown, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', schedule)
    window.addEventListener('mouseout', onOut)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('mouseout', onOut)
    }
  }, [rootRef, enabled, contentKey])

  return focal
}

/* -------------------------------------------------------- narration follow */

/**
 * While narration plays and the reader asked to follow along (the Listen
 * card's Follow), keep the paragraph being read in view: when it changes and
 * sits outside a comfortable band, scroll it gently to the focal line. Off by
 * default: the page never moves on its own unless the reader asks. A reader
 * who scrolls away themselves is left alone for a few seconds.
 */
export function useFollowNarration(rootRef: RefObject<HTMLElement | null>, activeId: string | null, playing: boolean): void {
  const lastUserScroll = useRef(0)

  useEffect(() => {
    const mark = () => {
      lastUserScroll.current = Date.now()
    }
    const onKey = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) mark()
    }
    window.addEventListener('wheel', mark, { passive: true })
    window.addEventListener('touchmove', mark, { passive: true })
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('wheel', mark)
      window.removeEventListener('touchmove', mark)
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  useEffect(() => {
    if (!playing || !activeId || Date.now() - lastUserScroll.current < 4000) return
    const el = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-focus]') ?? []).find((x) => x.dataset.focus === activeId)
    if (!el) return
    const r = el.getBoundingClientRect()
    const vh = window.innerHeight
    if (r.top >= vh * 0.15 && r.bottom <= vh * 0.75) return
    try {
      window.scrollTo({ top: window.scrollY + r.top - vh * FOCAL_LINE + 12, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
    } catch {
      /* no scrolling here (tests) */
    }
  }, [rootRef, activeId, playing])
}

/* --------------------------------------------------------- float neighbours */

/** The Reading light's tint box reaches this far past a paragraph (reader.css `.wr-r-lit::before`, inset -6px -10px). */
const TINT_PAD_X = 10
const TINT_PAD_Y = 6

/**
 * The Reading light's box for paragraphs beside a floated (wrapped) photo is
 * always a plain rectangle, never under the photo's tint:
 * - a paragraph wholly beside the photo gets a box as wide as its text: the
 *   tint is cut (`data-beside-float` + a `--wr-lit-clip` inset) to stop
 *   halfway into the gap before the photo;
 * - a paragraph that runs on below the photo keeps the full column box, and
 *   is marked `data-float-over` with the photo's block id, so the article
 *   keeps that photo at full strength on top while the paragraph is lit.
 * Re-measured whenever the article resizes (images loading, unfolds opening)
 * or the window does.
 */
export function useFloatNeighbours(rootRef: RefObject<HTMLElement | null>, contentKey: unknown): void {
  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return
    let raf = 0
    const mark = () => {
      raf = 0
      const floats = Array.from(root.querySelectorAll<HTMLElement>('figure[data-focus]'))
        .map((f) => ({ el: f, side: getComputedStyle(f).float }))
        .filter((f) => f.side === 'left' || f.side === 'right')
        .map((f) => {
          const r = f.el.getBoundingClientRect()
          const cs = getComputedStyle(f.el)
          // Halfway into the gap between the text and the photo (its inner margin), and below it (its bottom margin).
          const gap = (parseFloat(f.side === 'right' ? cs.marginLeft : cs.marginRight) || 0) / 2
          const below = (parseFloat(cs.marginBottom) || 0) / 2
          return { id: f.el.dataset.focus ?? '', side: f.side, top: r.top, bottom: r.bottom + below, left: r.left - gap, right: r.right + gap, height: r.height }
        })
        .filter((f) => f.height > 0)
      root.querySelectorAll<HTMLElement>('.wr-r-lit').forEach((el) => {
        const r = el.getBoundingClientRect()
        const f = floats.find((x) => r.top < x.bottom && r.bottom > x.top)
        const clear = () => {
          el.removeAttribute('data-beside-float')
          el.removeAttribute('data-float-over')
          el.style.removeProperty('--wr-lit-clip')
        }
        if (!f) return clear()
        // Runs on below the photo (more than a line of text under it): the full
        // column box, with the photo kept lit on top.
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 30
        if (r.bottom - f.bottom >= lineHeight) {
          clear()
          if (f.id) el.setAttribute('data-float-over', f.id)
          return
        }
        // Wholly beside the photo: a box only as wide as the text. The tint box
        // reaches TINT_PAD_X past the paragraph; cut its photo side back.
        const cut = f.side === 'right' ? r.right + TINT_PAD_X - f.left : f.right - (r.left - TINT_PAD_X)
        el.removeAttribute('data-float-over')
        el.setAttribute('data-beside-float', '')
        el.style.setProperty(
          '--wr-lit-clip',
          f.side === 'right' ? `inset(0 ${Math.max(0, Math.round(cut))}px 0 0 round 6px)` : `inset(0 0 0 ${Math.max(0, Math.round(cut))}px round 6px)`,
        )
      })
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(mark)
    }
    mark()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    ro?.observe(root)
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(raf)
      ro?.disconnect()
      window.removeEventListener('resize', schedule)
    }
  }, [rootRef, contentKey])
}

/* ------------------------------------------------------------ aside layout */

const ASIDE_GAP = 14

/**
 * Right-margin asides are positioned beside their paragraph, so a tall one
 * (an opened voice memo) can run into the next paragraph's. From 1024px up (the margin layout, index.css),
 * nudge each one down just enough to clear the one above. Re-run whenever
 * the article's size changes (images loading, unfolds, memos opening).
 */
export function useAsideLayout(rootRef: RefObject<HTMLElement | null>, contentKey: unknown): void {
  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return
    let raf = 0
    const layout = () => {
      raf = 0
      const asides = Array.from(root.querySelectorAll<HTMLElement>('.wr-aside-right'))
      asides.forEach((a) => (a.style.transform = ''))
      if (!window.matchMedia?.('(min-width: 1024px)').matches) return
      let floor = -Infinity
      const rects = asides.map((a) => a.getBoundingClientRect())
      asides.forEach((a, i) => {
        const r = rects[i]
        if (r.height === 0) return
        const shift = Math.max(0, floor + ASIDE_GAP - r.top)
        if (shift > 0) a.style.transform = `translateY(${Math.round(shift)}px)`
        floor = r.bottom + shift
      })
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(layout)
    }
    layout()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    ro?.observe(root)
    root.querySelectorAll('.wr-aside-right').forEach((a) => ro?.observe(a))
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(raf)
      ro?.disconnect()
      window.removeEventListener('resize', schedule)
    }
  }, [rootRef, contentKey])
}
